/**
 * OrejaProgressStepper - Visual progress indicator for Oreja background processing.
 *
 * Shows sequential steps with visual state indicators: completed (green check),
 * in-progress (spinner), or pending (circle). Switches between two step sets:
 * - Initial flow: Upload → Transcribe → Analyze
 * - Post-answer flow: Processing Answers → Generating Proposals
 */

import { Check, Circle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { OrejaPhase } from '@/lib/oreja/types';

export interface OrejaProgressStepperProps {
  /** Current phase of the Oreja session. */
  phase: OrejaPhase;
}

/** Ordered list of phases for determining step completion. */
const PHASE_ORDER: OrejaPhase[] = [
  'recording',    // 0
  'uploading',    // 1
  'transcribing', // 2
  'analyzing',    // 3
  'clarifying',   // 4
  'answering',    // 5
  'proposing',    // 6
  'reviewing',    // 7
  'refining',     // 8
  'committing',   // 9
  'committed',    // 10
];

interface StepDef {
  label: string;
  /** Phase index threshold — step is complete when current phase index > this value. */
  completeAfterIndex: number;
  /** Phase indices where this step is actively in progress. */
  activeAtIndices: number[];
  /** Approximate time estimate shown when this step is active (e.g., "~15-30s"). */
  estimate?: string;
}

/** Steps for the initial recording flow (Upload → Transcribe → Analyze). */
const INITIAL_STEPS: StepDef[] = [
  {
    label: 'Upload',
    completeAfterIndex: 1,
    activeAtIndices: [0, 1],
    estimate: '~5s',
  },
  {
    label: 'Transcribe',
    completeAfterIndex: 2,
    activeAtIndices: [2],
    estimate: '~15-30s',
  },
  {
    label: 'Analyze',
    completeAfterIndex: 3,
    activeAtIndices: [3],
    estimate: '~10-20s',
  },
];

/** Steps for after answering clarification questions. */
const PROPOSAL_STEPS: StepDef[] = [
  {
    label: 'Processing Answers',
    completeAfterIndex: 5, // complete after 'answering'
    activeAtIndices: [5],
    estimate: '~5s',
  },
  {
    label: 'Generating Proposals',
    completeAfterIndex: 6, // complete after 'proposing'
    activeAtIndices: [6],
    estimate: '~10-20s',
  },
];

/** Steps for the commit phase. */
const COMMIT_STEPS: StepDef[] = [
  {
    label: 'Pushing to ClickUp',
    completeAfterIndex: 9, // complete after 'committing'
    activeAtIndices: [9],
    estimate: '~5s',
  },
];

/** Phases that use the post-answer step set. */
const POST_ANSWER_PHASES: OrejaPhase[] = ['answering', 'proposing'];

/** Phases that use the commit step set. */
const COMMIT_PHASES: OrejaPhase[] = ['committing'];

/**
 * Returns the index of the given phase in the lifecycle order.
 *
 * @param phase - The current Oreja phase
 * @returns Numeric index (0-based), or -1 if not found
 */
function phaseIndex(phase: OrejaPhase): number {
  return PHASE_ORDER.indexOf(phase);
}

/**
 * Visual progress indicator shown during Oreja background processing phases.
 *
 * @param props - Component props containing the current phase
 * @returns Horizontal stepper with contextually appropriate steps
 */
export function OrejaProgressStepper({ phase }: OrejaProgressStepperProps) {
  const currentIndex = phaseIndex(phase);

  // Choose the right step set based on phase
  let steps: StepDef[];
  if (POST_ANSWER_PHASES.includes(phase)) {
    steps = PROPOSAL_STEPS;
  } else if (COMMIT_PHASES.includes(phase)) {
    steps = COMMIT_STEPS;
  } else {
    steps = INITIAL_STEPS;
  }

  return (
    <div className="flex items-center gap-3">
      {steps.map((step) => {
        const isComplete = currentIndex > step.completeAfterIndex;
        const isActive =
          !isComplete && step.activeAtIndices.includes(currentIndex);

        return (
          <div
            key={step.label}
            className="flex items-center gap-1.5 text-sm"
          >
            {isComplete ? (
              <Check className="h-4 w-4 text-emerald-500" />
            ) : isActive ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            ) : (
              <Circle className="h-4 w-4 text-muted-foreground" />
            )}
            <span
              className={cn(
                isComplete && 'text-muted-foreground line-through',
                isActive && 'font-semibold',
                !isComplete && !isActive && 'text-muted-foreground',
              )}
            >
              {step.label}
            </span>
            {isActive && step.estimate && (
              <span className="text-xs text-muted-foreground ml-1">
                ({step.estimate})
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
